import { type Infer, v } from 'convex/values'

import { type HabitMeasure, MEASURE_PRESETS, roundAmount } from './habitMeasure'
import { PER_DAY_PERIODS } from './schedule'

// Progressive overload for number habits: once an "at least" target is easy,
// suggest (or, opted in, automatically apply) a slightly higher one. A raised
// target takes effect the next day, so today's done state never changes.

export const MAX_HISTORY = 20
export const SNOOZE_DAYS = 14
export const DEFAULT_AUTO_EVERY_DAYS = 7
export const DEFAULT_MIN_HIT_PCT = 70
const WINDOW = 14
const MIN_HITS = 12
const STEADY_STREAK = 21
const OVERSHOOT = 1.1
const MAX_JUMP = 1.25

export const targetChangeValidator = v.object({
  // First logical day the new target applies.
  on: v.string(),
  from: v.number(),
  to: v.number(),
  auto: v.optional(v.boolean()),
})

export const autoLastValidator = v.object({
  on: v.string(),
  held: v.boolean(),
  hits: v.number(),
  total: v.number(),
  from: v.number(),
  to: v.number(),
})

export const autoProgressValidator = v.object({
  step: v.number(),
  everyDays: v.number(),
  // Day the last automatic (or accepted) raise took effect, or auto was turned on.
  lastBumpOn: v.string(),
  // Stop raising (atLeast) or lowering (atMost) at this value.
  cap: v.optional(v.number()),
  // Share of scheduled days in the period that must hit the target.
  minHitPct: v.optional(v.number()),
  // Outcome of the latest period check, for the habit sheet.
  last: v.optional(autoLastValidator),
})

export const levelUpValidator = v.object({
  snoozedUntil: v.optional(v.string()),
  maintaining: v.optional(v.boolean()),
  auto: v.optional(autoProgressValidator),
  // Accepted raise waiting for its first day; history already has its entry.
  next: v.optional(v.object({ target: v.number(), on: v.string() })),
  // Oldest first, capped at MAX_HISTORY.
  history: v.optional(v.array(targetChangeValidator)),
})

export type LevelUp = Infer<typeof levelUpValidator>
export type TargetChange = Infer<typeof targetChangeValidator>

const words = (s: string) => new Set(s.split(' '))
// Units in every locale's presets plus common free-typed ones.
const GROWABLE_UNITS = words(
  'reps rep push-ups pushups push-up sit-ups situps squats pull-ups pullups burpees lunges flexiones flexões pompes repeticiones repetições répétitions 回 次 个 個 ' +
    'pages page ページ 页 páginas chapters chapter chapitres capítulos 章 ' +
    'steps step 歩 步 pasos passos pas ' +
    'km kms mi miles mile kilometers kilometres m meters metres laps 公里 ' +
    'minutes minute min mins 分 分钟 分鐘 minutos ' +
    'hours hour h hr hrs 時間 小时 horas heures heure ' +
    'seconds second sec secs s 秒 segundos secondes ' +
    'words word 単語 字 mots palabras palavras problems questions',
)
const KG_UNITS = words('kg kgs kilo kilos キロ 公斤 千克 quilos kilogramos')
const LB_UNITS = words('lb lbs pound pounds libras livres')
const MAINTENANCE_UNITS = words(
  'glasses glass cups cup ml l liters litres oz 杯 vasos copos verres kcal cal calories calorie 千卡 kcals servings',
)
// Keeping steady is the point here, whatever the unit.
const MAINTENANCE_TITLE =
  /sleep|nap|water|hydrat|drink|睡眠|寝|眠|水|喝|dormir|sueño|sono|sommeil|agua|água|eau|boire|beber/i
const GROWABLE_TITLE =
  /push|squat|plank|pull|burpee|lunge|sit-?up|crunch|run|jog|walk|swim|cycl|bike|row|lift|read|write|study|practi[cs]e|meditat|腕立て|スクワット|プランク|読|書|走|ラン|俯卧撑|深蹲|平板|读|写|跑|flexi|sentadilla|correr|leer|escribir|pompe|courir|lire|écrire|agachamento|ler|escrever/i

const unitKey = (unit: string) => unit.trim().toLowerCase()

export function isGrowable(m: HabitMeasure, title: string): boolean {
  if (m.direction !== 'atLeast' || m.fast) return false
  const u = unitKey(m.unit)
  const preset = MEASURE_PRESETS.find((p) => p.measure.unit === u)
  if (preset) return preset.progressive && !MAINTENANCE_TITLE.test(title)
  if (MAINTENANCE_UNITS.has(u) || MAINTENANCE_TITLE.test(title)) return false
  return GROWABLE_UNITS.has(u) || KG_UNITS.has(u) || LB_UNITS.has(u) || GROWABLE_TITLE.test(title)
}

// Plate-sized steps for weights; null for other units.
export function plateStep(unit: string, target: number): number | null {
  const u = unitKey(unit)
  if (KG_UNITS.has(u)) return target < 20 ? 1.25 : 2.5
  if (LB_UNITS.has(u)) return target < 45 ? 2.5 : 5
  return null
}

// Rounding grid for a target's magnitude: 20 → 1, 50 → 5, 8000 → 500.
function grid(target: number) {
  const e = 10 ** Math.floor(Math.log10(target))
  const g = target / e < 3 ? e / 10 : e / 2
  return Number.isInteger(target) ? Math.max(1, g) : g
}
const snap = (n: number, g: number) => roundAmount(Math.round(n / g) * g)

// ~+10% on a sensible grid; when they routinely do more, their typical amount,
// capped at +25%.
export function suggestTarget(m: HabitMeasure, typical?: number): number {
  const t = m.target
  const plate = plateStep(m.unit, t)
  if (plate) return roundAmount(t + plate)
  const g = grid(t)
  const base = Math.max(snap(t * 1.1, g), roundAmount(t + g))
  if (!typical || typical < t * OVERSHOOT) return base
  const cap = roundAmount(Math.floor((t * MAX_JUMP) / g) * g)
  return Math.max(base, Math.min(snap(typical, g), cap))
}

// Target that applies on `date`: a pending raise from its day on, and for days
// before a recorded change the target they had back then.
export function targetOn(m: HabitMeasure, lu: LevelUp | undefined, date: string): number {
  if (lu?.next && date >= lu.next.on) return lu.next.target
  const later = lu?.history?.find((e) => e.on > date)
  return later ? later.from : m.target
}

export function measureOn(m: HabitMeasure, lu: LevelUp | undefined, date: string): HabitMeasure {
  const target = targetOn(m, lu, date)
  return target === m.target ? m : { ...m, target }
}

// Records a change from `change.on`, replacing any made for that day or later.
export function withChange(lu: LevelUp | undefined, change: TargetChange): LevelUp {
  const prev = lu?.history ?? []
  const from = prev.find((e) => e.on >= change.on)?.from ?? change.from
  const history = prev.filter((e) => e.on < change.on)
  if (from !== change.to) history.push({ ...change, from })
  return { ...lu, history: history.slice(-MAX_HISTORY) }
}

// One day of a habit: its stats day code (d done · m missed · …) and amount.
export type LevelUpDay = { date: string; code: string; amount: number }

const isScheduled = (d: LevelUpDay) => d.code === 'd' || d.code === 'm'

// Scheduled days since the target last changed, oldest first.
export function scheduledSinceChange(days: readonly LevelUpDay[], lu: LevelUp | undefined) {
  const since = lu?.history?.slice(-1)[0]?.on
  return days.filter((d) => isScheduled(d) && (!since || d.date >= since))
}

// ≥12 of the last 14 scheduled days hit (scaled when fewer are required) and
// none of the last 3 missed.
export function hitRateOk(sched: readonly LevelUpDay[], minDays = WINDOW): boolean {
  const w = sched.slice(-WINDOW)
  if (w.length < minDays || !w.length) return false
  const hits = w.filter((d) => d.code === 'd').length
  return hits / w.length >= MIN_HITS / WINDOW && w.slice(-3).every((d) => d.code === 'd')
}

export type LevelUpSuggestion = {
  from: number
  to: number
  hits: number
  window: number
  typical: number
  streak: number
  overshoot: boolean
  doneToday: boolean
  score: number
}

export function levelUpSuggestion(input: {
  title: string
  measure: HabitMeasure
  levelUp?: LevelUp
  period: string
  today: string
  days: readonly LevelUpDay[]
}): LevelUpSuggestion | null {
  const { measure: m, levelUp: lu, today } = input
  if (!isGrowable(m, input.title) || !PER_DAY_PERIODS.has(input.period)) return null
  if (lu?.maintaining || lu?.next || (lu?.snoozedUntil && today < lu.snoozedUntil)) return null
  // Only judge days since amounts started being logged: check-ins from before the
  // habit became a number habit carry no amount and say nothing about the target.
  const firstLogged = input.days.findIndex((d) => d.amount > 0)
  if (firstLogged < 0) return null
  const sched = scheduledSinceChange(input.days.slice(firstLogged), lu)
  if (!hitRateOk(sched)) return null
  const w = sched.slice(-WINDOW)
  const hitAmounts = w.filter((d) => d.code === 'd' && d.amount > 0).map((d) => d.amount)
  const typical = hitAmounts.length
    ? roundAmount(hitAmounts.reduce((a, b) => a + b, 0) / hitAmounts.length)
    : m.target
  let streak = 0
  for (let i = sched.length - 1; i >= 0 && sched[i].code === 'd'; i--) streak++
  const overshoot = typical >= m.target * OVERSHOOT
  if (!overshoot && streak < STEADY_STREAK) return null
  const to = suggestTarget(m, overshoot ? typical : undefined)
  if (to <= m.target) return null
  return {
    from: m.target,
    to,
    hits: w.filter((d) => d.code === 'd').length,
    window: w.length,
    typical,
    streak,
    overshoot,
    doneToday: input.days.some((d) => d.date === today && d.code === 'd'),
    score: overshoot ? 1 + typical / m.target : streak / STEADY_STREAK,
  }
}

export type AutoCheck = {
  outcome: 'raise' | 'hold' | 'atCap'
  to: number
  hits: number
  total: number
}

// Auto-progress: once `everyDays` have passed since the last raise, judge the
// period's scheduled days. Enough hits raises (atLeast) or lowers (atMost) the
// target by the step, up to the cap; otherwise it holds.
export function autoCheck(input: {
  measure: HabitMeasure
  levelUp?: LevelUp
  today: string
  days: readonly LevelUpDay[]
}): AutoCheck | null {
  const { measure: m, levelUp: lu, today } = input
  const auto = lu?.auto
  if (!auto || lu?.next || lu?.maintaining || m.fast) return null
  const elapsed =
    (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${auto.lastBumpOn}T00:00:00Z`)) / 86_400_000
  if (elapsed < auto.everyDays) return null
  const since = lu?.history?.slice(-1)[0]?.on ?? ''
  const sched = input.days.filter(
    (d) => isScheduled(d) && d.date >= auto.lastBumpOn && d.date >= since && d.date < today,
  )
  if (!sched.length) return null
  const hits = sched.filter((d) => d.code === 'd').length
  const total = sched.length
  const down = m.direction === 'atMost'
  const next = roundAmount(down ? m.target - auto.step : m.target + auto.step)
  const to =
    auto.cap === undefined ? next : down ? Math.max(next, auto.cap) : Math.min(next, auto.cap)
  if (to <= 0 || to === m.target || (down ? to > m.target : to < m.target))
    return { outcome: 'atCap', to: m.target, hits, total }
  const ok = hits / total >= (auto.minHitPct ?? DEFAULT_MIN_HIT_PCT) / 100
  return { outcome: ok ? 'raise' : 'hold', to: ok ? to : m.target, hits, total }
}

// Default step for the auto-increase setting: the size of a suggested raise.
export const defaultAutoStep = (m: HabitMeasure) => roundAmount(suggestTarget(m) - m.target)

// Coach prompt block for habits ready for a raise ('' when none).
export const levelUpCoachNote = (list: string) =>
  list
    ? `Number habits that look ready for a higher target (they've been hitting it easily):\n${list}\nAt most once per conversation, and only if it fits what they're talking about, kindly mention one and offer the raise as an option — never pressure, and accept "I'm happy where I am" without pushing. If they agree, call raise_habit_target (it starts tomorrow; today's progress is unaffected).`
    : ''
